<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn class="mx-2" fab x-small color="primary" @click="$router.back()">
            <v-icon dark>mdi-arrow-left</v-icon>
          </v-btn>
          <span>Edit Coupon</span>
        </v-card-title>
        <v-card-text>
          <div v-if="isLoading" class="text-center">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-2">Loading coupon...</p>
          </div>
          <div v-else-if="!coupon && !isLoading" class="text-center">
            <v-icon large color="error">mdi-alert-circle</v-icon>
            <p class="mt-2">Coupon not found</p>
            <v-btn color="primary" @click="$router.back()">Go Back</v-btn>
          </div>
          <div v-else>
            <v-row>
              <v-col sm="4">
                <v-text-field
                  v-model="form.code"
                  outlined label="Coupon Code *"
                  prepend-inner-icon="mdi-ticket-percent"
                  :error-messages="errors.code"
                  @input="form.code = form.code ? form.code.toUpperCase() : ''"
                ></v-text-field>
              </v-col>

              <v-col sm="4">
                <v-select
                  v-model="form.discount_type_id"
                  :items="discountTypeItems"
                  item-text="label"
                  item-value="id"
                  outlined label="Discount Type *"
                  :prepend-inner-icon="isPercentage ? 'mdi-percent' : 'mdi-currency-inr'"
                  :error-messages="errors.discount_type_id"
                  :loading="isLoadingDropdown"
                >
                  <template v-slot:item="{ item }">
                    <v-icon small class="mr-2">{{ item.code === 'PERCENTAGE' ? 'mdi-percent' : 'mdi-currency-inr' }}</v-icon>
                    {{ item.label }}
                  </template>
                </v-select>
              </v-col>

              <v-col sm="4">
                <v-text-field
                  v-model="form.discount_value"
                  outlined
                  :label="isPercentage ? 'Discount Percentage *' : 'Discount Amount (₹) *'"
                  prepend-inner-icon="mdi-numeric" type="number"
                  :error-messages="errors.discount_value"
                ></v-text-field>
              </v-col>

              <v-col sm="12">
                <v-textarea
                  v-model="form.description"
                  outlined label="Description (Optional)"
                  rows="3" prepend-inner-icon="mdi-text"
                  :error-messages="errors.description"
                ></v-textarea>
              </v-col>

              <v-col sm="4">
                <v-text-field
                  v-model="form.min_order_amount"
                  outlined label="Minimum Order Amount (₹)"
                  prepend-inner-icon="mdi-currency-inr" type="number"
                  :error-messages="errors.min_order_amount"
                ></v-text-field>
              </v-col>

              <v-col sm="4" v-if="isPercentage">
                <v-text-field
                  v-model="form.max_discount_amount"
                  outlined label="Max Discount Amount (₹)"
                  prepend-inner-icon="mdi-currency-inr" type="number"
                  :error-messages="errors.max_discount_amount"
                ></v-text-field>
              </v-col>

              <v-col sm="4">
                <v-text-field
                  v-model="form.usage_limit"
                  outlined label="Usage Limit (blank = unlimited)"
                  prepend-inner-icon="mdi-counter" type="number"
                  :error-messages="errors.usage_limit"
                ></v-text-field>
              </v-col>

              <v-col sm="4">
                <v-menu v-model="validFromMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.valid_from" label="Valid From"
                      prepend-inner-icon="mdi-calendar" outlined readonly clearable
                      :error-messages="errors.valid_from"
                      v-bind="attrs" v-on="on"
                      @click:clear="form.valid_from = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.valid_from" @input="validFromMenu = false"></v-date-picker>
                </v-menu>
              </v-col>

              <v-col sm="4">
                <v-menu v-model="validUntilMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="form.valid_until" label="Valid Until"
                      prepend-inner-icon="mdi-calendar" outlined readonly clearable
                      :error-messages="errors.valid_until"
                      v-bind="attrs" v-on="on"
                      @click:clear="form.valid_until = null"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="form.valid_until" :min="form.valid_from" @input="validUntilMenu = false"></v-date-picker>
                </v-menu>
              </v-col>

              <v-col sm="4">
                <v-select
                  v-model="form.position_id"
                  :items="positionItems"
                  item-text="name"
                  item-value="id"
                  outlined
                  label="Applicable For"
                  prepend-inner-icon="mdi-account-badge"
                  clearable
                  :loading="isLoadingDropdown"
                  :error-messages="errors.position_id"
                ></v-select>
              </v-col>

              <v-col sm="4">
                <v-switch
                  class="mt-0 ml-5" v-model="form.is_active" inset
                  :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                ></v-switch>
              </v-col>
            </v-row>

            <!-- Coupon Information Card -->
            <v-card class="mt-4" outlined>
              <v-card-title>
                <v-icon left>mdi-information</v-icon>
                Coupon Information
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col sm="4"><strong>Times Used:</strong><br />{{ coupon.used_count || 0 }}</v-col>
                  <v-col sm="4"><strong>Created:</strong><br />{{ formatDate(coupon.created_at) }}</v-col>
                  <v-col sm="4"><strong>Last Updated:</strong><br />{{ formatDate(coupon.updated_at) }}</v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <!-- User Assignment Card -->
            <v-card class="mt-4" outlined>
              <v-card-title class="d-flex align-center flex-wrap">
                <v-icon left>mdi-account-group</v-icon>
                User Assignment
                <v-spacer></v-spacer>
                <div class="d-flex align-center">
                  <v-chip
                    small outlined
                    :color="form.is_global ? 'success' : 'primary'"
                    class="mr-3"
                  >
                    <v-icon left x-small>{{ form.is_global ? 'mdi-earth' : 'mdi-account-multiple' }}</v-icon>
                    {{ form.is_global ? 'Everyone' : `${userCoupons.length} user(s)` }}
                  </v-chip>
                  <span class="text-body-2 grey--text mr-2">For Everyone</span>
                  <v-switch
                    v-model="form.is_global"
                    inset color="success" hide-details class="mt-0"
                    @change="onToggleGlobal"
                  ></v-switch>
                  <span class="text-body-2 grey--text ml-2">Specific Users</span>
                </div>
              </v-card-title>

              <v-divider></v-divider>

              <v-card-text>
                <template v-if="form.is_global">
                  <v-alert type="success" text>
                    <strong>Available to all users.</strong>
                    Toggle the switch above to restrict this coupon to specific users only.
                  </v-alert>
                </template>

                <template v-else>
                  <!-- Add user row -->
                  <v-row align="center" class="mb-4">
                    <v-col cols="12" sm="8">
                      <v-autocomplete
                        v-model="selectedUserId"
                        :items="filteredAvailableUsers"
                        item-text="display_name"
                        item-value="id"
                        label="Search by phone number"
                        outlined dense clearable hide-details
                        prepend-inner-icon="mdi-phone"
                        :loading="isLoadingUsers"
                        no-data-text="No users found"
                      >
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-icon x-small class="mr-1" color="grey">mdi-phone</v-icon>
                              {{ item.phone || 'No phone' }}
                            </v-list-item-title>
                            <v-list-item-subtitle class="text-caption">
                              {{ item.user_name || item.first_name }}
                              <span v-if="item.email" class="ml-1 grey--text">· {{ item.email }}</span>
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                    <v-col cols="12" sm="4">
                      <v-btn
                        color="primary"
                        :loading="isAddingUser"
                        :disabled="!selectedUserId || isAddingUser"
                        @click="addUser"
                      >
                        <v-icon left>mdi-account-plus</v-icon>
                        Assign User
                      </v-btn>
                    </v-col>
                  </v-row>

                  <!-- Loading state -->
                  <div v-if="isLoadingUserCoupons" class="text-center py-6">
                    <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                    <p class="mt-3 grey--text">Loading assigned users...</p>
                  </div>

                  <!-- Empty state -->
                  <v-alert v-else-if="userCoupons.length === 0" type="warning" text>
                    No users assigned yet. Search and assign users above.
                  </v-alert>

                  <!-- Assigned users table -->
                  <v-simple-table v-else dense>
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left">#</th>
                          <th class="text-left">Username</th>
                          <th class="text-left">Email</th>
                          <th class="text-left">Status</th>
                          <th class="text-left">Assigned On</th>
                          <th class="text-center">Remove</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(uc, i) in userCoupons" :key="uc.id">
                          <td>{{ i + 1 }}</td>
                          <td>
                            <div class="d-flex align-center">
                              <v-avatar size="28" color="primary" class="mr-2">
                                <span class="white--text caption">
                                  {{ uc.user ? uc.user.user_name.charAt(0).toUpperCase() : '?' }}
                                </span>
                              </v-avatar>
                              {{ uc.user ? uc.user.user_name : '—' }}
                            </div>
                          </td>
                          <td class="grey--text">{{ uc.user ? (uc.user.email || '—') : '—' }}</td>
                          <td>
                            <status-chip x-small :color="uc.status === 1 ? 'success' : uc.status === 2 ? 'error' : 'primary'" :label="String(uc.status === 1 ? 'Used' : uc.status === 2 ? 'Expired' : 'Active')" />
                          </td>
                          <td class="grey--text caption">{{ formatDate(uc.created_at) }}</td>
                          <td class="text-center">
                            <v-btn
                              icon x-small color="error"
                              :loading="removingId === uc.id"
                              :disabled="removingId !== null"
                              @click="removeUserCoupon(uc)"
                            >
                              <v-icon small>mdi-close-circle</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </template>
              </v-card-text>
            </v-card>

            <v-divider class="mt-5 mb-5"></v-divider>
            <v-row>
              <v-col sm="12">
                <v-btn :loading="isSaving" :disabled="isSaving" color="primary" @click="updateCoupon" class="mr-4">
                  <v-icon left>mdi-content-save</v-icon>
                  Update Coupon
                </v-btn>
                <v-btn color="secondary" @click="$router.back()" :disabled="isSaving" class="mr-4">
                  <v-icon left>mdi-cancel</v-icon>
                  Cancel
                </v-btn>
                <v-btn color="error" @click="confirmDelete" :disabled="isSaving" outlined>
                  <v-icon left>mdi-delete</v-icon>
                  Delete Coupon
                </v-btn>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <!-- Success Dialog -->
      <v-dialog v-model="showSuccessDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: green; font-size: 90px; width: 200px !important">mdi-check-circle</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: green">Success!</h1>
                      <br />
                      <h3>Coupon updated successfully.</h3>
                      <br />
                      <v-btn color="success" @click="showSuccessDialog = false">OK</v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" width="600">
        <v-card rounded="xxl">
          <v-container>
            <v-row align="center">
              <v-col sm="3" align="center">
                <v-icon large style="color: red; font-size: 90px; width: 200px !important">mdi-alert-circle</v-icon>
              </v-col>
              <v-col sm="9" align="center">
                <v-card-text>
                  <v-row>
                    <v-col>
                      <h1 style="color: red">Confirm Delete</h1>
                      <br />
                      <h3>Are you sure you want to delete this coupon?</h3>
                      <br />
                      <v-btn color="error" @click="deleteCoupon" :loading="isDeleting" class="mr-4">Yes, Delete</v-btn>
                      <v-btn color="secondary" @click="showDeleteDialog = false" :disabled="isDeleting">Cancel</v-btn>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-col>
            </v-row>
          </v-container>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";

export default {
  name: "CouponEditPage",

  data() {
    return {
      coupon: null,
      form: {
        code: "",
        discount_type_id: null,
        discount_value: "",
        description: "",
        min_order_amount: "",
        max_discount_amount: "",
        usage_limit: "",
        valid_from: null,
        valid_until: null,
        is_active: true,
        position_id: null,
        is_global: true,
      },
      errors: {},
      isLoading: true,
      isSaving: false,
      isDeleting: false,
      showSuccessDialog: false,
      showDeleteDialog: false,
      validFromMenu: false,
      validUntilMenu: false,
      discountTypes: [],
      positions: [],
      isLoadingDropdown: false,
      // User assignment
      userCoupons: [],
      availableUsers: [],
      isLoadingUserCoupons: false,
      isLoadingUsers: false,
      isAddingUser: false,
      selectedUserId: null,
      removingId: null,
    };
  },

  computed: {
    positionItems() {
      return [{ id: null, name: 'All Users' }, ...this.positions];
    },
    filteredAvailableUsers() {
      const assignedIds = this.userCoupons.map((uc) => uc.user_id);
      return this.availableUsers.filter((u) => !assignedIds.includes(u.id));
    },
    discountTypeItems() {
      return this.discountTypes.map((dt) => ({
        ...dt,
        label: dt.code === "PERCENTAGE" ? `${dt.description} (%)` : `${dt.description} (₹)`,
      }));
    },
    selectedDiscountType() {
      return this.discountTypes.find((d) => d.id === this.form.discount_type_id) || null;
    },
    isPercentage() {
      const dt = this.selectedDiscountType;
      if (!dt) return false;
      const text = (dt.code || dt.description || "").toLowerCase();
      return text.includes("percent");
    },
  },

  mounted() {
    this.fetchCoupon();
    this.fetchCouponDropdown();
  },

  methods: {
    async fetchCoupon() {
      this.isLoading = true;
      try {
        const couponId = this.$route.params.id;
        const response = await axios.get(`/coupons/${couponId}`);

        if (response.data.success) {
          this.coupon = response.data.data;
          this.form = {
            code: this.coupon.code,
            discount_type_id: this.coupon.discount_type_id,
            discount_value: this.coupon.discount_value,
            description: this.coupon.description || "",
            min_order_amount: this.coupon.min_order_amount || "",
            max_discount_amount: this.coupon.max_discount_amount || "",
            usage_limit: this.coupon.usage_limit || "",
            valid_from: this.coupon.valid_from ? this.coupon.valid_from.substring(0, 10) : null,
            valid_until: this.coupon.valid_until ? this.coupon.valid_until.substring(0, 10) : null,
            is_active: this.coupon.is_active,
            position_id: this.coupon.position_id || null,
            is_global: this.coupon.is_global !== undefined ? this.coupon.is_global : true,
          };
          this.fetchUserAssignments();
          this.fetchAvailableUsers();
        } else {
          this.$store.dispatch("errors/setError", { message: "Coupon not found" });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 404) {
          this.$store.dispatch("errors/setError", { message: "Coupon not found" });
        } else if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", { message: "Error loading coupon data" });
        }
      } finally {
        this.isLoading = false;
      }
    },

    async fetchUserAssignments() {
      this.isLoadingUserCoupons = true;
      try {
        const res = await axios.get("/user_coupons", {
          params: { coupon_id: this.coupon.id, rowsPerPage: 1000 },
        });
        if (res.data.success) {
          this.userCoupons = res.data.data;
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingUserCoupons = false;
      }
    },

    async fetchCouponDropdown() {
      this.isLoadingDropdown = true;
      try {
        const res = await axios.get("/coupons/dropdown");
        if (res.data.success) {
          this.discountTypes = res.data.data.discount_types || [];
          this.positions = res.data.data.positions || [];
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingDropdown = false;
      }
    },

    async fetchAvailableUsers() {
      this.isLoadingUsers = true;
      try {
        const res = await axios.get("/user_coupons/dropdown");
        if (res.data.success) {
          this.availableUsers = (res.data.data.users || []).map((u) => ({
            ...u,
            display_name: u.phone ? String(u.phone) : (u.user_name || ''),
          }));
        }
      } catch (e) {
        console.error(e);
      } finally {
        this.isLoadingUsers = false;
      }
    },

    async addUser() {
      if (!this.selectedUserId) return;
      this.isAddingUser = true;
      try {
        const res = await axios.post("/user_coupons", {
          coupon_id: this.coupon.id,
          user_id: this.selectedUserId,
        });
        if (res.data.success) {
          this.selectedUserId = null;
          await this.fetchUserAssignments();
        } else {
          this.$store.dispatch("errors/setError", {
            message: res.data.message || "Failed to assign user.",
          });
        }
      } catch (e) {
        const msg =
          e.response?.data?.errors?.coupon_id?.[0] ||
          e.response?.data?.message ||
          "Failed to assign user.";
        this.$store.dispatch("errors/setError", { message: msg });
      } finally {
        this.isAddingUser = false;
      }
    },

    async removeUserCoupon(uc) {
      this.removingId = uc.id;
      try {
        const encId = this.encryptIt(uc.id);
        const res = await axios.post(`/user_coupons/delete/${encId}`);
        if (res.data.success) {
          this.userCoupons = this.userCoupons.filter((u) => u.id !== uc.id);
          if (this.userCoupons.length === 0) this.isSpecificUsers = false;
        }
      } catch (e) {
        this.$store.dispatch("errors/setError", { message: "Failed to remove user assignment." });
      } finally {
        this.removingId = null;
      }
    },

    onToggleGlobal(val) {
      if (!val && this.userCoupons.length > 0) {
        this.$nextTick(() => {
          this.form.is_global = false;
        });
      }
      if (val && this.userCoupons.length > 0) {
        this.$nextTick(() => {
          this.form.is_global = true;
        });
        this.$store.dispatch("errors/setError", {
          message: "Remove all assigned users first to make this coupon available to everyone.",
        });
        this.$nextTick(() => {
          this.form.is_global = false;
        });
      }
    },

    async updateCoupon() {
      this.isSaving = true;
      this.errors = {};
      try {
        const couponData = {
          code: this.form.code,
          discount_type_id: this.form.discount_type_id,
          discount_value: this.form.discount_value,
          description: this.form.description || null,
          min_order_amount: this.form.min_order_amount || null,
          max_discount_amount: this.form.max_discount_amount || null,
          usage_limit: this.form.usage_limit || null,
          valid_from: this.form.valid_from || null,
          valid_until: this.form.valid_until || null,
          is_active: this.form.is_active,
          position_id: this.form.position_id || null,
          is_global: this.form.is_global,
        };

        const couponId = this.$route.params.id;
        const response = await axios.patch(`/coupons/${couponId}`, couponData);

        if (response.data.success) {
          this.showSuccessDialog = true;
          this.coupon = response.data.data;
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "An error occurred while updating the coupon.",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response) {
          if (error.response.status === 422) {
            this.errors = error.response.data.errors || {};
          } else if (error.response.status === 401) {
            this.$router.push("/login");
          } else {
            this.$store.dispatch("errors/setError", {
              message: error.response.data.message || "An error occurred while updating the coupon.",
            });
          }
        } else {
          this.$store.dispatch("errors/setError", {
            message: "Network error. Please check your connection and try again.",
          });
        }
      } finally {
        this.isSaving = false;
      }
    },

    confirmDelete() {
      this.showDeleteDialog = true;
    },

    async deleteCoupon() {
      this.isDeleting = true;
      try {
        const couponId = this.$route.params.id;
        const response = await axios.post(`/coupons/delete/${couponId}`);
        if (response.data.success) {
          this.showDeleteDialog = false;
          this.$router.push("/coupons");
        } else {
          this.$store.dispatch("errors/setError", {
            message: response.data.message || "An error occurred while deleting the coupon.",
          });
        }
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 401) {
          this.$router.push("/login");
        } else {
          this.$store.dispatch("errors/setError", {
            message: "An error occurred while deleting the coupon.",
          });
        }
      } finally {
        this.isDeleting = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },

  watch: {
    "form.code"() {
      if (this.errors.code) this.$delete(this.errors, "code");
    },
    "form.discount_value"() {
      if (this.errors.discount_value) this.$delete(this.errors, "discount_value");
    },
  },
};
</script>

<style scoped>
.v-card-title {
  font-weight: bold;
  font-size: 1.5rem;
}
</style>
